import '../../../../styles/knowledge-concept/quiz/TopicSelection.css';

import {
	Box,
	Button,
	Checkbox,
	IconButton,
	InputBase,
	Paper,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { createTest, setExamId } from '../../../../store/testSelectionSlice.js';
import { setSection, updateQuestionData } from '../../../../store/ExamSlice.js';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { LOCAL_BASE_URL } from '../../../../constants/constants.js';
import Label from '../../../atoms/Label.js';
import SearchIcon from '@mui/icons-material/Search';
import axiosRequestInterceptor from '../../../../interceptor/axiosAPIRequestInterceptor.js';
import { fetchLearningMapDetails } from '../../../../services/DashboardApi.js';
import { logger } from '../../../../utils/logger';
import useIsMobile from '../../../../hooks/ismobile.js';

const TopicSelection = ({ handleGenerateQuiz, setSidebarState }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const [selectedSubTopics, setSelectedSubTopics] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [subtopicWidth, setSubtopicWidth] = useState(null);
	const [topics, setTopics] = useState([]);
	const [height, setHeight] = useState(0);
	const selectedMainTab = useSelector((state) => state.mainTab.tab_name);
	const { selectedTest } = useSelector((state) => state.testSelection);
	const { userId } = useSelector((state) => state.userDetails);
	const scrollRefs = useRef({});
	const subtopicRef = useRef(null);
	let isMobile = useIsMobile();
	const api = axiosRequestInterceptor;

	const scrollHorizontally = (index, offset) => {
		if (scrollRefs.current[index]) {
			scrollRefs.current[index].scrollBy({
				left: offset,
				behavior: 'smooth',
			});
		}
	};

	useEffect(() => {
		const updateWidth = () => {
			if (subtopicRef.current) {
				const leftOffset = subtopicRef.current.getBoundingClientRect().left; // Distance from left viewport edge
				let availableWidth = ((window.innerWidth - leftOffset) / 16).toFixed(2); // Remaining width
				availableWidth -= 3.81;

				logger.log('Left Offset:', leftOffset);
				logger.log('Available Width:', availableWidth);
				setSubtopicWidth(availableWidth); // Update width state
			}
		};

		// Delay the first calculation to ensure layout is ready
		const timeout = setTimeout(updateWidth, 100);

		window.addEventListener('resize', updateWidth); // Update on resize
		return () => {
			clearTimeout(timeout);
			window.removeEventListener('resize', updateWidth);
		};
	}, [topics]);

	useEffect(() => {
		const fetchData = async () => {
			//let userId = "1049";
			let subject = selectedMainTab;
			try {
				const response = await fetchLearningMapDetails(userId, subject);
				setTopics(response.data);
				logger.log('Topics from API: ', response.data);
			} catch (error) {
				console.error('Error fetching topics: ', error);
			}
		};

		fetchData();
	}, [selectedMainTab]);

	const handleTopicChange = (event, subTopics, topic) => {
		const { checked } = event.target;
		if (checked) {
			setSelectedSubTopics((prev) => {
				const existingTopicIndex = prev.findIndex((t) => t.topicId === topic);

				if (existingTopicIndex >= 0) {
					const updatedTopics = [...prev];
					const newSubtopic = subTopics.map((element) => ({
						id: element,
						count: 1,
					}));
					updatedTopics[existingTopicIndex].subtopics = [
						updatedTopics[existingTopicIndex].subtopics,
						...newSubtopic,
					];

					return updatedTopics;
				} else {
					return [
						...prev,
						{
							topicId: topic,
							subtopics: subTopics.map((element) => ({
								id: element,
								count: 1,
							})),
						},
					];
				}
			});
		} else {
			setSelectedSubTopics((prev) => {
				const topicIndex = prev.findIndex((element) => element.topicId === topic);
				if (topicIndex >= 0) {
					prev.splice(topicIndex, 1);
				}
				return [...prev];
			});
		}
	};

	const handleSubTopicChange = (event, subTopicId, topicId) => {
		const { checked } = event.target;
		logger.log('called');

		if (checked) {
			setSelectedSubTopics((prev) => {
				// Find if topic already exists
				const existingTopicIndex = prev.findIndex((t) => t.topicId === topicId);

				if (existingTopicIndex >= 0) {
					// Add subtopic to existing topic
					const updatedTopics = [...prev];
					const subtopicSet = new Set(
						updatedTopics[existingTopicIndex].subtopics.map((sub) => sub.id)
					);

					if (!subtopicSet.has(subTopicId)) {
						// Only add if not already present
						updatedTopics[existingTopicIndex].subtopics.push({
							id: subTopicId,
							count: 1,
						});
					}
					logger.log('called check exist');
					return updatedTopics;
				} else {
					// Create new topic entry
					logger.log('called check not exist');
					return [
						...prev,
						{
							topicId: topicId,
							subtopics: [{ id: subTopicId, count: 1 }],
						},
					];
				}
			});
		} else {
			setSelectedSubTopics((prev) => {
				logger.log('called not check');
				return prev
					.map((topic) => {
						if (topic.topicId === topicId) {
							return {
								...topic,
								subtopics: topic.subtopics.filter((st) => st.id !== subTopicId),
							};
						}
						return topic;
					})
					.filter((topic) => topic.subtopics?.length > 0);
			});
		}
	};

	logger.log('Topics: ', topics);
	const filteredTopics = topics?.filter((topic) => {
		const topicMatch = topic.TOPIC_NAME.toLowerCase().includes(searchQuery.toLowerCase());
		const subtopicMatch = topic.SUBTOPICS?.some((subtopic) =>
			subtopic.SUBTOPIC_NAME.toLowerCase().includes(searchQuery.toLowerCase())
		);
		return topicMatch || subtopicMatch;
	});

	// Update pagination to use filtered topics
	const paginatedTopics = filteredTopics?.map((topic) => {
		return {
			...topic,
			// displayedSubtopics: topic.SUBTOPICS?.filter((subtopic) => subtopic.SUBTOPIC_NAME.toLowerCase().includes(searchQuery.toLowerCase())).slice(start, end),
			displayedSubtopics: topic.SUBTOPICS?.filter((subtopic) =>
				subtopic.SUBTOPIC_NAME.toLowerCase().includes(searchQuery.toLowerCase())
			),
		};
	});
	logger.log('Paginated Topics: ', paginatedTopics);

	// Update search input handler
	const handleSearch = (e) => {
		setSearchQuery(e.target.value);
	};

	const creatTest = async () => {
		// try {
		// 	const response = await api.post(`/sft/createSFTTest`, {
		// 		userId: userId,
		// 		testType: selectedTest,
		// 		questionCount: 2,
		// 		difficulty: "easy",
		// 		topicDistribution: selectedSubTopics,
		// 	});
		// 	if (!response.data.success) {
		// 		console.warn("API Warning:", response.data.message); // Log warning without crashing
		// 		return null; // Prevent UI error popups
		// 	}
		// 	logger.log("Test created", response.data);
		// 	return response.data;
		// } catch (error) {
		// 	console.error("Error creating test: ", error?.response?.data?.message);
		// 	// throw error;
		// }
		const createSFT = async (payload) => {
			try {
				const response = await api.post(`/sft/createSFTTest`, {
					userId: userId,
					// testType: selectedMainTab,
					// topicDistribution: selectedSubTopics,
					...payload,
				});
				if (!response.data.success) {
					console.warn('API Warning:', response.data.message); // Log warning without crashing
					return null; // Prevent UI error popups
				}
				logger.log('Test created', response.data?.test?.questions);
				dispatch(setExamId(response.data?.testId));
				dispatch(updateQuestionData(response.data?.test));
				dispatch(setSection(section));
				navigate('/test-instructions');
			} catch (error) {
				console.error('Error creating test: ', error?.response?.data?.message);
				// throw error;
				alert(error?.response?.data?.message);
			}
		};
		const payload = {
			testType: selectedMainTab,
			topicDistribution: selectedSubTopics,
		};
		const sections = { READING_WRITING: 1, MATHS: 2 };
		const section = sections?.[selectedMainTab];
		dispatch(createTest(payload));
		createSFT(payload);
	};

	useEffect(() => {
		for (let index = 0; index < paginatedTopics.length; index++) {
			const topic = paginatedTopics[index];
			logger.log(
				'topic ',
				topic,
				': ',
				selectedSubTopics
					.find((topics) => topics?.topicId === topic?.TOPIC_ID)
					?.subtopics.every((element) =>
						topic.displayedSubtopics.every((e) => {
							return e.SUBTOPIC_ID === element.id;
						})
					)
			);
		}
	}, [selectedSubTopics]);

	useEffect(() => {
		const currentLocation = location.pathname;
		logger.log('Current Location: ', currentLocation);
		let hei = 0;
		if (currentLocation === '/SFT/create') {
			hei += !isMobile ? 64 + 180 : 56 + 240;
		} else if (currentLocation === '/knowledge_concept') {
			hei += !isMobile ? 192.8 + 180 : 56 + 208 + 134.8;
		}
		setHeight(hei);
	}, []);

	const hasAllSubtopics = (topicId) => {
		// Find the topic in selectedSubTopics
		const selectedTopic = selectedSubTopics?.find((topic) => topic.topicId === topicId);
		if (!selectedTopic) return false; // If topic is not in selectedSubTopics, return false

		// Find the topic in paginatedTopics
		const paginatedTopic = paginatedTopics?.find((topic) => topic.TOPIC_ID === topicId);
		if (!paginatedTopic) return false; // If topic is not in paginatedTopics, return false

		// Convert selected subtopics to a Set for quick lookup
		const selectedSubtopicIds = new Set(
			selectedTopic.subtopics?.map((subtopic) => subtopic.id)
		);

		// Check if every subtopic in paginatedTopic exists in selectedTopic
		return paginatedTopic?.SUBTOPICS?.every((subtopic) =>
			selectedSubtopicIds.has(subtopic.SUBTOPIC_ID)
		);
	};

	return (
		<Box
			sx={{
				padding: { xs: '1rem 1rem 1rem 1rem', sm: '2rem 0rem 1rem 2rem' },
				paddingTop: '1rem',
				fontFamily: 'Arial',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: { xs: 'center', sm: 'space-between' },
				}}
			>
				<Typography variant="h4" sx={{ color: '#1e1ea9', paddingBottom: '1rem' }}>
					Topic Selection
				</Typography>
				<Paper
					component="form"
					sx={{
						p: '0px 4px',
						display: { xs: 'none', sm: 'flex' },
						alignItems: 'center',
						width: { xs: '10rem', sm: '15rem' },
						marginTop: '1rem',
						marginBottom: {
							xs: '1rem',
							sm: '0rem',
						},
						marginRight: {
							xs: 0,
							sm: '3.8rem',
						},
						borderColor: '#000',
						border: 1,
						borderRadius: '15px',
					}}
				>
					<InputBase
						sx={{ ml: 1, flex: 1 }}
						placeholder="Search"
						inputProps={{ 'aria-label': 'Search Topic or Sub Topic' }}
						onChange={handleSearch}
					/>
					<IconButton type="button" sx={{ p: '10px', flex: 1 }} aria-label="search">
						<SearchIcon />
					</IconButton>
				</Paper>
			</Box>
			<Box
				sx={{
					display: { xs: 'flex', sm: 'none' },
					// alignSelf: "end",
					// alignItems: "end",
					flexDirection: 'row',
					justifyContent: {
						xs: `${
							location.pathname === '/knowledge_concept' ? 'space-between' : 'end'
						}`,
						sm: 'end',
					},
					width: '100%',
				}}
			>
				<Paper
					component="form"
					sx={{
						p: '0px 4px',
						display: 'flex',
						alignItems: 'center',
						width: { xs: '10rem', sm: '15rem' },
						marginTop: {
							xs: '1rem',
							sm: '0rem',
						},
						marginBottom: {
							xs: '1rem',
							sm: '0rem',
						},
						marginRight: {
							xs: 0,
							sm: '3.8rem',
						},
						borderColor: '#000',
						border: 1,
						borderRadius: '15px',
					}}
				>
					<InputBase
						sx={{ ml: 1, flex: 1 }}
						placeholder="Search"
						inputProps={{ 'aria-label': 'Search Topic or Sub Topic' }}
						onChange={handleSearch}
					/>
					<IconButton type="button" sx={{ p: '10px', flex: 1 }} aria-label="search">
						<SearchIcon />
					</IconButton>
				</Paper>
				{location.pathname === '/knowledge_concept' && (
					<Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
						<Button
							variant="contained"
							onClick={() => setSidebarState(true)}
							sx={{
								backgroundColor: '#000',
								marginTop: {
									xs: '1rem',
									sm: '0rem',
								},
								marginBottom: {
									xs: '1rem',
									sm: '0rem',
								},
							}}
						>
							Notebook
						</Button>
					</Box>
				)}
			</Box>
			<Box
				sx={{
					marginTop: '1rem',
					display: 'flex',
					flexDirection: 'row',
					gap: '20px',
					height: {
						xs: `calc(100vh - ${height / 16}rem)`,
						sm: `calc(100vh - ${height / 16}rem)`,
					},
					overflowY: 'auto',
					whiteSpace: 'nowrap',
					scrollbarWidth: 'none',
					position: 'relative',
					overflowX: { xs: 'hidden', sm: '' },
				}}
			>
				{topics?.length === 0 ? (
					<Box className="no-data-message">
						<Label
							label="No topics available for the selected tab."
							fontSize="20px"
							fontWeight="500"
							color="gray"
						/>
					</Box>
				) : (
					// <Box sx={{ height: "65vh", overflow: "hidden" }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: '20px',
							height: 'fit-content',
							width: '100%',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '1.25rem',
								width: '100%',
							}}
						>
							{paginatedTopics?.map((topic, topicIndex) => (
								<Box
									key={topicIndex}
									className="topic_row"
									sx={{
										// flexWrap: { xs: "wrap", sm: "nowrap" },
										flexDirection: { xs: 'column', sm: 'row' },
										justifyContent: { xs: 'center', sm: 'start' },
										alignItems: 'start',
										marginBottom: { xs: '1.5rem', sm: '0rem' },
									}}
								>
									<Tooltip
										placement="top-start"
										title={topic.TOPIC_NAME}
										followCursor
									>
										<Box>
											<label className="topic_card">
												{/* <input
												type='checkbox'
												value={topic.TOPIC_NAME}
												onChange={(event) => {
													handleTopicChange(
														event,
														topic.SUBTOPICS?.map(
															(sub) => sub.SUBTOPIC_ID
														),
														topic.TOPIC_ID
													);
												}}
												checked={hasAllSubtopics(topic?.TOPIC_ID)}
											/> */}
												<Checkbox
													{...{
														inputProps: {
															'aria-label': 'Checkbox demo',
														},
													}}
													value={topic.TOPIC_NAME}
													onChange={(event) => {
														handleTopicChange(
															event,
															topic.SUBTOPICS?.map(
																(sub) => sub.SUBTOPIC_ID
															),
															topic.TOPIC_ID
														);
													}}
													checked={hasAllSubtopics(topic?.TOPIC_ID)}
												/>
												<p
													style={{
														marginLeft: '8px',
														fontSize: '1rem',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{topic.TOPIC_NAME}
												</p>
											</label>
										</Box>
									</Tooltip>

									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
										}}
									>
										<Box
											className="test-pagination-buttons"
											sx={{ display: { xs: 'none', sm: 'flex' } }}
										>
											{
												<IconButton
													onClick={() =>
														scrollHorizontally(topicIndex, -200)
													}
												>
													<KeyboardArrowLeftIcon
														fontSize="large"
														sx={{
															color: 'black',
														}}
													/>
												</IconButton>
											}
										</Box>
										<Box
											ref={subtopicRef}
											sx={{
												overflow: 'hidden',
												maxWidth: {
													xs: 'calc(100vw - 2rem)',
													sm: subtopicWidth
														? `${subtopicWidth}rem`
														: 'auto',
												},
												width: {
													xs: 'calc(100vw - 2rem)',
													sm: subtopicWidth
														? `${subtopicWidth}rem`
														: 'auto',
												},
											}}
										>
											<Box
												ref={(el) => (scrollRefs.current[topicIndex] = el)} // Store the reference for this row
												sx={{
													display: 'flex',
													flexDirection: 'column',
													gap: '20px',
													width: '100%', // Changed to 90% of viewport width
													overflowX: 'auto',
													whiteSpace: 'nowrap',
													scrollbarWidth: 'none', // Hides scrollbar (Firefox)
													'&::-webkit-scrollbar': { display: 'none' }, // Hides scrollbar (Chrome, Safari)
												}}
											>
												<Box className="sub_topic_grid">
													{topic.displayedSubtopics?.map(
														(subTopic, subTopicIndex) => (
															<Tooltip
																placement="top-start"
																followCursor
																title={subTopic.SUBTOPIC_NAME}
															>
																<label
																	key={subTopicIndex}
																	className="sub_topic_card"
																>
																	{/* <input
																	type='checkbox'
																	value={subTopic.SUBTOPIC_NAME}
																	checked={selectedSubTopics?.some(
																		(topic) =>
																			topic.subtopics.some(
																				(subtopics) =>
																					subtopics.id ===
																					subTopic.SUBTOPIC_ID
																			)
																	)}
																	onChange={(e) => {
																		handleSubTopicChange(
																			e,
																			subTopic.SUBTOPIC_ID,
																			topic.TOPIC_ID
																		);
																	}}
																/> */}
																	<Checkbox
																		{...{
																			inputProps: {
																				'aria-label':
																					'Checkbox demo',
																			},
																		}}
																		value={
																			subTopic.SUBTOPIC_NAME
																		}
																		checked={selectedSubTopics?.some(
																			(topic) =>
																				topic.subtopics.some(
																					(subtopics) =>
																						subtopics.id ===
																						subTopic.SUBTOPIC_ID
																				)
																		)}
																		onChange={(e) => {
																			handleSubTopicChange(
																				e,
																				subTopic.SUBTOPIC_ID,
																				topic.TOPIC_ID
																			);
																		}}
																		sx={{
																			color: '#fff',
																			'&.Mui-checked': {
																				color: '#fff',
																			},
																		}}
																	/>

																	<p
																		style={{
																			fontSize: '1rem',
																			overflow: 'hidden',
																			textOverflow:
																				'ellipsis',
																			whiteSpace: 'nowrap',
																		}}
																	>
																		{subTopic.SUBTOPIC_NAME}
																	</p>
																</label>{' '}
															</Tooltip>
														)
													)}
												</Box>
											</Box>
										</Box>
										<Box
											className="test-pagination-buttons"
											sx={{ display: { xs: 'none', sm: 'flex' } }}
										>
											{
												<IconButton
													aria-label="next"
													onClick={() =>
														scrollHorizontally(topicIndex, 200)
													}
												>
													<KeyboardArrowRightIcon
														fontSize="large"
														sx={{
															color: 'black',
														}}
													/>
												</IconButton>
											}
										</Box>
									</Box>
								</Box>
							))}
						</Box>
					</Box>
					// </Box>
				)}
			</Box>

			{topics?.length > 0 && (
				<Box style={{ marginTop: '0.74rem' }}>
					<button onClick={creatTest} className="generate_btn">
						Generate Quiz
					</button>
				</Box>
			)}
		</Box>
	);
};

export default TopicSelection;
